// /proyectos/:id/consultas — la bandeja de consultas del propietario del
// proyecto (ADR-0145, fase 2 corte 1, #0738). Hermana de
// /proyectos/:id/observaciones: mismo esqueleto (encabezado del proyecto,
// KPIs-interruptor, tabla con enlace estirado, paginación en el cliente),
// pero sobre `GET /owner-inquiries?assetGroupId=`.
//
// Quién la ve lo decide el servidor (jefe_proyecto en sus proyectos,
// gerente_postventa en toda la org; 403/404 para el resto). El estado de
// cada consulta también lo deriva el servidor; acá no se compara `dueAt`
// con el reloj para decidirlo. Las etiquetas visibles salen de
// `OWNER_INQUIRY_ESTADO_LABEL` vía lib/postventa/consultasList.
//
// Lo que navega vive en la URL: `?estado=`, `?pagina=`, `?tam=` y
// `?consulta=<id>`, que abre el hilo en el drawer — es el deep link del
// correo «Responder en el dashboard» al jefe.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  PLAZO_RESPUESTA_PROPIETARIO_DIAS,
  type OrgMember,
  type OwnerInquiryEstado,
  type OwnerInquiryListItem,
} from "@bitacorapro/api-types";
import { ConsultaDrawer } from "../components/ConsultaDrawer";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/icons";
import { PageError } from "../components/PageError";
import { PageSkeleton } from "../components/PageSkeleton";
import { Pager } from "../components/Pager";
import { Skel } from "../components/Skel";
import { ApiError } from "../lib/api/error";
import {
  useCarteraProyectoDetail,
  useMemberships,
  useOwnerInquiries,
  useRecipe,
} from "../lib/api/queries";
import {
  clampPage,
  contarPorEstado,
  ESTADOS,
  ESTADO_TONE,
  esReabierta,
  estadoLabel,
  filtrarPorEstado,
  ordenarConsultas,
  PAGE_SIZES,
  pageSlice,
  parseConsultasState,
  plazoRespuesta,
  totalPages,
  writeConsultasState,
  type ConsultasPatch,
  type PageSize,
} from "../lib/postventa/consultasList";
import { fechaCorta } from "../lib/postventa/observacionesList";
import { resolveUserDisplayName } from "../lib/util";
import { useQueryView } from "../lib/loading/useQueryView";
import { RefreshNotices, SlowNotice } from "./queryViewKit";

const COLUMNAS = ["Propiedad", "Propietario", "Consulta", "Recibida", "Plazo", "Estado"];

const TONE_PILL: Record<string, string> = {
  ok: "st-pill--ok",
  info: "st-pill--info",
  warn: "st-pill--warn",
  crit: "st-pill--crit",
  idle: "st-pill--idle",
};

const KPI_HINT: Record<OwnerInquiryEstado, string> = {
  sin_responder: "Dentro del plazo de respuesta",
  vencida: `Más de ${PLAZO_RESPUESTA_PROPIETARIO_DIAS} días sin respuesta`,
  respondida: "Respondimos su último mensaje",
};

/** El propietario que preguntó, resuelto contra el roster; «Propietario»
 *  si el roster no lo trae todavía. */
function propietarioDe(
  c: OwnerInquiryListItem,
  roster: ReadonlyMap<string, Pick<OrgMember, "displayName">>
): string {
  if (!c.authorUserId || !roster.has(c.authorUserId)) return "Propietario";
  return resolveUserDisplayName(c.authorUserId, roster);
}

function resultados(n: number): string {
  return n === 0 ? "Sin resultados" : n === 1 ? "1 consulta" : `${n} consultas`;
}

function useAnnouncer() {
  const [msg, setMsg] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );
  const announce = useCallback((m: string) => {
    setMsg("");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setMsg(m), 500);
  }, []);
  return [msg, announce] as const;
}

function TablaSkeleton() {
  return (
    <div className="panel__body--flush">
      <table className="dtable dtable--compact">
        <thead>
          <tr>
            {COLUMNAS.map((c) => (
              <th key={c} scope="col">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[0, 1, 2, 3, 4].map((i) => (
            <tr key={i}>
              <td>
                <Skel width={150} height={12} />
              </td>
              <td>
                <Skel width={110} height={12} />
              </td>
              <td>
                <div className="skel-row">
                  <Skel width="80%" height={12} />
                  <Skel width="50%" height={9} />
                </div>
              </td>
              <td>
                <Skel width={56} height={12} />
              </td>
              <td>
                <Skel width={96} height={20} pill />
              </td>
              <td>
                <Skel width={88} height={20} pill />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ConsultasDelProyectoRoute() {
  const { id } = useParams<{ id: string }>();
  const { data: recipe } = useRecipe();
  // Sólo el encabezado necesita el proyecto; la lista viene de la bandeja.
  const detailQuery = useCarteraProyectoDetail(id, { limit: 1 });
  // ADR-0166: carga / error / no encontrado por el contrato común. Un
  // `:id` ausente deja el detalle deshabilitado → «no encontrado», nunca
  // un skeleton eterno.
  const view = useQueryView(detailQuery);

  const groupLabel = recipe?.labels.assetGroup ?? "Proyecto";
  const groupPlural = recipe?.labels.assetGroupPlural ?? "Proyectos";
  const sub = `Preguntas de los propietarios. Respuesta en ${PLAZO_RESPUESTA_PROPIETARIO_DIAS} días corridos.`;

  const noEncontrado = (
    <div className="page-pad">
      <h1 className="page-header__title">{groupLabel} no encontrado</h1>
      <p className="page-header__sub">
        El id <code>{id}</code> no corresponde a ningún {groupLabel.toLowerCase()} de tu
        organización.
      </p>
      <Link to="/proyectos" className="btn btn--secondary">
        <Icon.Arrow s={14} /> Volver a {groupPlural.toLowerCase()}
      </Link>
    </div>
  );

  if (view.kind === "error") {
    if (view.error instanceof ApiError && view.error.status === 404) return noEncontrado;
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__lead">
            <div className="page-header__eyebrow">
              <Link to="/proyectos">{groupPlural}</Link> / {groupLabel}
            </div>
            <h1 className="page-header__title">Consultas</h1>
            <p className="page-header__sub">{sub}</p>
          </div>
        </div>
        <div className="panel">
          {view.offline ? (
            <PageError
              title="Sin conexión"
              description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
              onRetry={view.retry}
            />
          ) : (
            <PageError
              title={`No pudimos cargar el ${groupLabel.toLowerCase()}`}
              description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
              onRetry={view.retry}
            />
          )}
        </div>
      </div>
    );
  }

  if (view.kind === "loading") {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__lead">
            <div className="page-header__eyebrow">
              <Link to="/proyectos">{groupPlural}</Link> / <Skel width={140} height={10} />
            </div>
            <h1 className="page-header__title">Consultas</h1>
            <p className="page-header__sub">{sub}</p>
          </div>
        </div>
        <PageSkeleton label="Cargando consultas…" visible={view.visible}>
          <div className="kpi-grid kpi-grid--consultas" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="kpi-tile kpi-tile--dense">
                <div className="skel-row">
                  <Skel width="45%" height={9} />
                  <Skel width="32%" height={22} />
                  <Skel width="60%" height={9} />
                </div>
              </div>
            ))}
          </div>
          <div className="panel">
            <TablaSkeleton />
          </div>
        </PageSkeleton>
        {view.slow && <SlowNotice onRetry={() => void detailQuery.refetch()} />}
      </div>
    );
  }

  if (view.kind !== "ready" || !id) return noEncontrado;

  const { group } = view.data;
  return (
    <ConsultasDelProyecto
      assetGroupId={id}
      groupLabel={groupLabel}
      header={{ groupPlural, name: group.name, subline: group.subline ?? null, sub }}
    />
  );
}

/** La bandeja: tres KPIs-interruptor por estado, tabla paginada y el
 *  drawer del hilo abierto por `?consulta=`. Con `header` pinta también el
 *  encabezado de página (la ruta lo pasa; los tests del panel no). */
export function ConsultasDelProyecto({
  assetGroupId,
  groupLabel,
  header,
}: {
  assetGroupId: string;
  groupLabel: string;
  header?: { groupPlural: string; name: string; subline: string | null; sub: string };
}) {
  const inquiriesQuery = useOwnerInquiries(assetGroupId);
  // ADR-0166: la bandeja pasa por el contrato de carga. Una revisión que
  // falla sobre la lista ya cargada la conserva y avisa en línea.
  const listView = useQueryView(inquiriesQuery);
  const data = listView.kind === "ready" ? listView.data : undefined;
  const failed = listView.kind === "error";
  const refetch = () => void inquiriesQuery.refetch();
  const memberships = useMemberships();
  const [live, announce] = useAnnouncer();

  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseConsultasState(searchParams), [searchParams]);

  const roster = useMemo(
    () =>
      new Map<string, Pick<OrgMember, "displayName">>(
        (memberships.data?.members ?? []).map((m) => [m.userId, m])
      ),
    [memberships.data]
  );

  const inquiries = useMemo(() => data?.inquiries ?? [], [data]);
  // «Hoy» se fija con cada carga: el plazo se recalcula al refrescar, no
  // a cada render.
  const now = useMemo(() => new Date(), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const counts = useMemo(() => contarPorEstado(inquiries), [inquiries]);
  const filtered = useMemo(
    () => ordenarConsultas(filtrarPorEstado(inquiries, state.estado)),
    [inquiries, state.estado]
  );
  const pages = totalPages(filtered.length, state.tam);
  const pagina = clampPage(state.pagina, filtered.length, state.tam);
  const pageRows = pageSlice(filtered, pagina, state.tam);

  function update(patch: ConsultasPatch) {
    setSearchParams((prev) => writeConsultasState(prev, patch), { replace: true });
  }
  function patchAndAnnounce(patch: ConsultasPatch, prefix = "") {
    const next = parseConsultasState(writeConsultasState(searchParams, patch));
    update(patch);
    announce(`${prefix}${resultados(filtrarPorEstado(inquiries, next.estado).length)}`);
  }

  // La URL canónica: una página fuera de rango se corrige con replace.
  const listo = listView.kind === "ready";
  useEffect(() => {
    if (!listo) return;
    if (state.pagina !== pagina) update({ pagina });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listo, pagina, state.pagina]);

  const kpiValue = (n: number) => (failed ? "—" : n);
  const kpiTile = (k: OwnerInquiryEstado, tone: "" | "review" | "crit") => {
    const activo = state.estado === k;
    return (
      <button
        key={k}
        type="button"
        className={`kpi-tile kpi-tile--dense kpi-tile--link${tone ? ` kpi-tile--${tone}` : ""}`}
        aria-pressed={activo}
        onClick={() =>
          activo
            ? patchAndAnnounce({ estado: null }, "Filtro quitado. ")
            : patchAndAnnounce({ estado: k }, `Filtro: ${estadoLabel(k)}. `)
        }
      >
        <div className="kpi-tile__label">{estadoLabel(k)}</div>
        <div className="kpi-tile__value">
          <span>{kpiValue(counts[k])}</span>
        </div>
        <div className="kpi-tile__hint">{KPI_HINT[k]}</div>
        <span className="kpi-tile__chev" aria-hidden="true">
          <Icon.ChevronRight s={18} />
        </span>
      </button>
    );
  };
  const kpiTone = (k: OwnerInquiryEstado): "" | "review" | "crit" => {
    if (failed || counts[k] === 0) return "";
    if (k === "vencida") return "crit";
    if (k === "sin_responder") return "review";
    return "";
  };

  const hrefDe = (c: OwnerInquiryListItem) =>
    `?${writeConsultasState(searchParams, { consulta: c.id }).toString()}`;

  return (
    <div className="page">
      {header && (
        <div className="page-header">
          <div className="page-header__lead">
            <div className="page-header__eyebrow">
              <Link to="/proyectos">{header.groupPlural}</Link> /{" "}
              <Link to={`/proyectos/${assetGroupId}`}>
                {header.name}
                {header.subline ? ` — ${header.subline}` : ""}
              </Link>
            </div>
            <h1 className="page-header__title" tabIndex={-1}>
              Consultas
            </h1>
            <p className="page-header__sub">{header.sub}</p>
          </div>
          <div className="page-header__actions">
            <Link to={`/proyectos/${assetGroupId}`} className="btn btn--secondary btn--sm">
              Ver unidades
            </Link>
            <Link
              to={`/proyectos/${assetGroupId}/observaciones`}
              className="btn btn--secondary btn--sm"
            >
              Ver observaciones
            </Link>
          </div>
        </div>
      )}

      <section className="kpi-grid kpi-grid--consultas kpi-grid--obs" aria-label="Resumen">
        {ESTADOS.map((k) => kpiTile(k, kpiTone(k)))}
      </section>

      <div className="panel" style={{ marginBottom: 20 }}>
        {/* Barra como la de observaciones: el filtro activo (si hay) a la
            izquierda, el conteo a la derecha. */}
        <div className="filters">
          {state.estado !== null ? (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => patchAndAnnounce({ estado: null }, "Filtro quitado. ")}
            >
              Estado: {estadoLabel(state.estado)} · Quitar
            </button>
          ) : (
            <span className="cell-meta">Todas las consultas del {groupLabel.toLowerCase()}</span>
          )}
          <div className="filter-spacer" />
          {listo && <span className="cell-meta mono">{resultados(filtered.length)}</span>}
        </div>

        {listView.kind === "ready" && (
          <RefreshNotices
            refreshFailed={listView.refreshFailed}
            revalidatingHint={listView.revalidatingHint}
            onRetry={refetch}
          />
        )}

        {listView.kind === "loading" ? (
          <>
            {/* Sección: la pantalla ya tiene su único role=status (el
                anunciador de abajo), así que el skeleton no anuncia. */}
            <PageSkeleton
              label="Cargando consultas…"
              visible={listView.visible}
              announce={false}
              kind="section"
            >
              <TablaSkeleton />
            </PageSkeleton>
            {listView.slow && <SlowNotice onRetry={refetch} />}
          </>
        ) : listView.kind === "error" ? (
          <div className="panel__body--flush">
            {listView.offline ? (
              <PageError
                title="Sin conexión"
                description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
                onRetry={listView.retry}
              />
            ) : (
              <PageError
                title="No pudimos cargar las consultas"
                description="Revisa tu conexión e intenta de nuevo."
                onRetry={listView.retry}
              />
            )}
          </div>
        ) : listView.kind !== "ready" ? null : filtered.length === 0 ? (
          <div className="panel__body--flush">
            {state.estado !== null ? (
              <EmptyState
                title={`No hay consultas en «${estadoLabel(state.estado)}»`}
                description="Prueba con otro estado o quita el filtro."
                action={
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => patchAndAnnounce({ estado: null }, "Filtro quitado. ")}
                  >
                    Quitar filtro
                  </button>
                }
              />
            ) : (
              // Sin CTA a propósito: las consultas las escriben los
              // propietarios desde su unidad.
              <EmptyState
                title={`Todavía no hay consultas de propietarios en este ${groupLabel.toLowerCase()}.`}
                description="Aparecerán aquí cuando un propietario escriba desde su propiedad."
              />
            )}
          </div>
        ) : (
          <>
            {/* `--obs`: bloque contenedor de los .sr-only y contenedor de
                consulta; `--rowlink-row`: el enlace estirado cubre la
                fila (el título no es la primera columna). Sin ambos el
                ::after de .row-title sobresalía del scroller. */}
            <div
              className="dtable-scroll dtable-scroll--obs"
              tabIndex={0}
              role="region"
              aria-label="Tabla de consultas"
            >
              <table className="dtable dtable--compact dtable--rowlink dtable--rowlink-row dtable--no-check">
                <caption className="sr-only">Consultas del {groupLabel.toLowerCase()}</caption>
                <thead>
                  <tr>
                    <th scope="col">Propiedad</th>
                    <th scope="col">Propietario</th>
                    <th scope="col" className="col-title">
                      Consulta
                    </th>
                    <th scope="col">Recibida</th>
                    <th scope="col">Plazo</th>
                    <th scope="col" className="col-estado">
                      Estado
                    </th>
                    <th scope="col" className="col-chev">
                      <span className="sr-only">Abrir</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((c) => {
                    const plazo = plazoRespuesta(c, now);
                    const nueva = c.unreadCount > 0;
                    return (
                      <tr key={c.id} className={nueva ? "is-new" : undefined}>
                        <td className="cell-strong">{c.propiedadLabel}</td>
                        <td className="cell-meta">{propietarioDe(c, roster)}</td>
                        <td className="col-title">
                          <div className="cell-with-sub">
                            <Link
                              to={hrefDe(c)}
                              className="cell-strong cell-strong--link row-title"
                              title={c.excerpt}
                            >
                              {nueva && (
                                <>
                                  <span className="consulta-dot" aria-hidden="true" />
                                  <span className="sr-only">Nueva: </span>
                                </>
                              )}
                              {c.excerpt}
                            </Link>
                            {c.conversion ? (
                              // ADR-0163 — la consulta pasó a observación.
                              <span className="cell-sub">
                                Convertida en observación {c.conversion.folio ?? ""}
                              </span>
                            ) : (
                              esReabierta(c) && (
                                <span className="cell-sub">
                                  Nuevo mensaje del propietario · {fechaCorta(c.lastEntryAt)}
                                </span>
                              )
                            )}
                          </div>
                        </td>
                        <td className="mono">{fechaCorta(c.createdAt)}</td>
                        <td>
                          <span className={`st-pill ${TONE_PILL[plazo.tone]}`}>{plazo.texto}</span>
                        </td>
                        <td className="col-estado">
                          <span className={`st-pill ${TONE_PILL[ESTADO_TONE[c.estado]]}`}>
                            {estadoLabel(c.estado)}
                          </span>
                        </td>
                        <td className="cell-meta col-chev" aria-hidden="true">
                          <Icon.ChevronRight s={16} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pager
              numbered
              currentPage={pagina}
              totalPages={pages}
              totalItems={filtered.length}
              pageSize={state.tam}
              onPage={(n) => {
                update({ pagina: n });
                announce(`Página ${n} de ${pages}`);
              }}
              pageSizes={PAGE_SIZES}
              onPageSize={(n) => update({ tam: n as PageSize })}
            />
          </>
        )}
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {live}
      </p>

      {state.consulta && (
        <ConsultaDrawer
          key={state.consulta}
          inquiryId={state.consulta}
          onClose={() => update({ consulta: null })}
        />
      )}
    </div>
  );
}
